import {
  CustomerIndexViewModel,
  CustomerViewModel,
  TenantViewModel,
  UnitAccountIndexViewModel,
  UnitAccountViewModel,
} from '@ail/customers-api'
import {
  ApprovalProcessConfigViewModel,
  ApprovalProcessStatus,
  ApprovalProcessStepKind,
  ApprovalProcessStepStatus,
  ApprovalProcessStepViewModel,
  OrderInputModel,
  OrderViewModel,
  OrdersFormViewModel,
} from '@ail/orders-api'
import React, { Component } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { ConnectedProps, connect } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { FrontendSettings } from '../../../helpers/FrontendSettings'
import { printDate } from '../../../helpers/Utils'
import { wrapQuery } from '../../../redux/api/apiQuery'
import { customersApi } from '../../../redux/api/customerApi'
import { ordersApi } from '../../../redux/api/ordersApi'
import { State } from '../../../redux/reducers/initialState'
import { PL001, emptyGuid, mobileVersion } from '../../../types/constants'
import { FrontendSettingsKeys } from '../../../types/enums'
import { Orders } from '../../../utils/orders'
import Confirm from '../../common/Confirm/Confirm'
import './RequestList.scss'
import { Arr } from '../../../helpers/Arr'
import { Loader } from '../../common/Loader/Loader'

const statusFilters = {
  all: (r: GridRow) => !!r,
  active: (r: GridRow) =>
    r.order.approvalProcess?.status === 'Approved' &&
    (!r.order.internalOrderStatus ||
      r.order.internalOrderStatus === 'New' ||
      r.order.internalOrderStatus === 'InProgress' ||
      r.order.internalOrderStatus === 'Pending' ||
      r.order.internalOrderStatus === 'OnHold'),
  draft: (r: GridRow) => !r.order.approvalProcess,
  sent: (r: GridRow) =>
    r.order.approvalProcess?.status === 'Approved' &&
    !!r.approvalProcessConfig?.steps?.at(0)?.autoApprove &&
    (!r.order.internalOrderStatus || r.order.internalOrderStatus === 'New'),
  archived: (r: GridRow) => !!r.order.archived,
  pending: (r: GridRow) => r.order.approvalProcess?.status === 'Pending',
  approved: (r: GridRow) =>
    r.order.approvalProcess?.status === 'Approved' &&
    !r.approvalProcessConfig?.steps?.at(0)?.autoApprove &&
    r.order.internalOrderStatus !== 'Done' &&
    r.order.internalOrderStatus !== 'Cancelled' &&
    r.order.internalOrderStatus !== 'Archived',
  declined: (r: GridRow) => r.order.approvalProcess?.status === 'Declined',
  cancelled: (r: GridRow) =>
    r.order.approvalProcess?.status === 'Approved' && r.order.internalOrderStatus === 'Cancelled',
  completed: (r: GridRow) =>
    r.order.approvalProcess?.status === 'Approved' &&
    (r.order.internalOrderStatus === 'Done' || r.order.internalOrderStatus === 'Processed'),
}

const companyFilter = (r: GridRow, companyId?: string) =>
  !companyId ||
  r.order.company?.companyName.toLowerCase().includes(companyId.toLowerCase()) ||
  r.order.userDisplayName?.toLowerCase().includes(companyId.toLowerCase()) ||
  `${r.order?.data['DR002']?.toLowerCase()} ${r.order?.data['DR004']?.toLowerCase()}`.includes(
    companyId.toLowerCase()
  ) ||
  r.order.data['LTR_driver']?.toLowerCase().includes(companyId.toLowerCase()) ||
  r.order.data['InternalReference']?.toLowerCase().includes(companyId.toLowerCase())

const formFilter = (r: GridRow, formId?: string) => !formId || r.form?.id.toLowerCase() === formId.toLowerCase()

const tenantFilter = (r: GridRow, tenantId?: string) =>
  !tenantId || r.form?.ownerTenant.toLowerCase() === tenantId.toLowerCase()

interface GridRow {
  order: OrderViewModel
  form: OrdersFormViewModel
  approvalProcessConfig?: ApprovalProcessConfigViewModel
}

interface StepsToGrid {
  rowItem: GridRow
  mainSteps: ApprovalProcessStepViewModel[]
  approvedSteps?: number
  pendingSteps?: number
  declinedSteps?: number
  otherSteps: ApprovalProcessStepViewModel[]
  unusedSteps: number[]
  autoApprove: boolean
}

interface RequestListState {
  orders: OrderViewModel[]
  orderForms: OrdersFormViewModel[]
  tenants: TenantViewModel[]
  customers: CustomerIndexViewModel[]
  units: UnitAccountIndexViewModel[]
  showMobileStatuses: boolean
  expandedOrders: string[]
  showDeleteConfirm: boolean
  selectedRequest: StepsToGrid | null
  showDuplicateConfirm: boolean
  filteredOrders: GridRow[]
  stepsInGrid: StepsToGrid[]
  filteredSteps: StepsToGrid[]
  requestFilter: IRequestFilter
  page: number
  paging: IPagingItem[]
  numberOfPages: number
  loading: boolean
  orderFormDict: Partial<Record<string, OrdersFormViewModel>>
  approvalProcessConfigDict: Partial<Record<string, ApprovalProcessConfigViewModel>>
  orderFormTenantDict: { [id: string]: boolean }
  orderFormsForActions: OrdersFormViewModel[]
  currentUserCustomerMap: Partial<Record<string, CustomerViewModel>>
  currentUserUnitMap: Partial<Record<string, UnitAccountViewModel>>
  types: string[]
  statuses: string[]
  language: string
  availableStatusFilters: Partial<typeof statusFilters>
}

interface IPagingItem {
  page: number
  title: string
  disabled?: boolean
}

type RequestListProps = ConnectedProps<typeof connector> & WithTranslation

interface IRequestFilter {
  groupInFilter: string
  ownerInFilter: string
  companyInFilter: string
  typeInFilter: string
  statusInFilter: keyof typeof statusFilters
  rws: number
}

class RequestList extends Component<RequestListProps, RequestListState> {
  constructor(props: RequestListProps) {
    super(props)

    const emptyRequsetFilter: IRequestFilter = {
      groupInFilter: '',
      ownerInFilter: '',
      companyInFilter: '',
      typeInFilter: '',
      statusInFilter: 'active',
      rws: 14,
    }

    const requestFilter =
      FrontendSettings.getObject<IRequestFilter>(FrontendSettingsKeys.requestFilter) ?? emptyRequsetFilter

    this.state = {
      orders: [],
      orderForms: [],
      tenants: [],
      customers: [],
      units: [],
      showMobileStatuses: false,
      expandedOrders: [],
      selectedRequest: null,
      showDeleteConfirm: false,
      showDuplicateConfirm: false,
      loading: false,
      filteredOrders: [],
      stepsInGrid: [],
      requestFilter,
      page: 1,
      paging: [],
      numberOfPages: 0,
      filteredSteps: [],
      orderFormDict: {},
      orderFormTenantDict: {},
      approvalProcessConfigDict: {},
      orderFormsForActions: [],
      currentUserCustomerMap: {},
      currentUserUnitMap: {},
      types: [],
      statuses: [],
      language: this.props.i18n.language,
      availableStatusFilters: {},
    }
  }

  public componentDidMount() {
    this.reload()

    if (this.props.user?.isInternalUser) {
      const requestFilter = { ...this.state.requestFilter }

      requestFilter.statusInFilter = requestFilter.statusInFilter ?? 'approved'

      if (mobileVersion) {
        requestFilter.rws = 14
      } else {
        if (requestFilter.rws === 14) {
          requestFilter.rws = 50
        }
      }

      this.setState({
        requestFilter,
      })
    }
  }

  private reload() {
    this.setState({ loading: true })
    Promise.all([
      this.props.getOrders(),
      this.props.getOrdersForms(),
      this.props.getCustomers(),
      this.props.getUnits(),
      this.props.user?.isInternalUser ? this.props.loadTenants() : Promise.resolve([]),
    ])
      .then(([orders, orderForms, customers, units, tenants]) => {
        const currentUserCustomerMap = Arr.toHashMap(
          customers,
          (c) => c.customer!.id,
          (c) => c.customer!
        )
        const currentUserUnitMap = Arr.toHashMap(
          units,
          (c) => c.unit!.id,
          (c) => c.unit!
        )
        this.setState({ orders, orderForms, customers, units, tenants, currentUserCustomerMap, currentUserUnitMap })
      })
      .finally(() => {
        this.setState({ loading: false })
      })
  }

  private saveRequestFilter() {
    const requestFilterToSave = this.state.requestFilter
    FrontendSettings.setObject(FrontendSettingsKeys.requestFilter, requestFilterToSave)
  }

  public componentDidUpdate(oldProps: RequestListProps, oldState: RequestListState) {
    const { i18n } = this.props
    const { language, orderForms, tenants, customers } = this.state
    const { groupInFilter, rws } = this.state.requestFilter

    if (this.state.requestFilter !== oldState.requestFilter) {
      this.saveRequestFilter()
    }

    if (i18n.language !== language) {
      this.setState({
        language: i18n.language,
      })
    }

    if (
      tenants !== oldState.tenants ||
      customers != oldState.customers ||
      language !== oldState.language ||
      orderForms !== oldState.orderForms ||
      groupInFilter !== oldState.requestFilter.groupInFilter ||
      this.state.orderFormTenantDict !== oldState.orderFormTenantDict
    ) {
      this.prepareFormsToRender()
    }

    if (oldState.orderForms !== this.state.orderForms) {
      const orderFormDict: { [id: string]: OrdersFormViewModel } = {}
      const orderFormTenantDict: { [id: string]: boolean } = {}
      const approvalProcessConfigDict: { [id: string]: ApprovalProcessConfigViewModel } = {}
      this.state.orderForms.forEach((form) => {
        orderFormDict[form.id] = form
        orderFormTenantDict[form.ownerTenant] = true
        form.approvalProcessConfigs.forEach((ac) => {
          approvalProcessConfigDict[ac.id] = ac
        })
      })
      this.setState({
        orderFormDict,
        orderFormTenantDict,
        approvalProcessConfigDict,
      })
    }
    if (oldState.filteredOrders !== this.state.filteredOrders) {
      this.ordersInGrid()
    }
    if (rws !== oldState.requestFilter.rws || this.state.filteredSteps !== oldState.filteredSteps) {
      this.setState({
        numberOfPages:
          this.state.filteredSteps.length > rws && rws > 0 ? Math.ceil(this.state.filteredSteps.length / rws) : 1,
      })
    }

    if (
      this.state.orders !== oldState.orders ||
      this.state.orderForms !== oldState.orderForms ||
      this.state.orderFormDict !== oldState.orderFormDict ||
      this.state.requestFilter.statusInFilter !== oldState.requestFilter.statusInFilter ||
      this.state.requestFilter.typeInFilter !== oldState.requestFilter.typeInFilter ||
      this.state.requestFilter.groupInFilter !== oldState.requestFilter.groupInFilter ||
      this.state.requestFilter.companyInFilter !== oldState.requestFilter.companyInFilter ||
      this.state.requestFilter.ownerInFilter !== oldState.requestFilter.ownerInFilter
    ) {
      this.filterOrders()
    }

    if (this.state.numberOfPages !== oldState.numberOfPages || this.state.page !== oldState.page) {
      if (Number.isNaN(this.state.numberOfPages)) {
        this.setState({
          numberOfPages: 0,
        })
      }
      const { numberOfPages, page } = this.state
      const visiblePagingRange = Math.min(3, numberOfPages - 1)
      const visiblePagingHalfRange = Math.trunc(visiblePagingRange / 2)

      let c = 0
      let l = page - visiblePagingHalfRange
      let r = page + visiblePagingHalfRange

      if (l <= 0) {
        c = 1 - l
      }
      if (r >= numberOfPages) {
        c = numberOfPages - r
      }

      l = l + c
      r = r + c

      const pagingInt: IPagingItem[] = []
      let i = 0

      if (page > 1) {
        pagingInt.push({
          page: page - 1,
          title: 'Prev',
        })
      }

      const onePlus = 1 + visiblePagingHalfRange
      if (page > onePlus && visiblePagingHalfRange >= 1 && !mobileVersion) {
        pagingInt.push({
          page: 1,
          title: '1',
        })

        pagingInt.push({
          page: 0,
          title: '...',
          disabled: true,
        })
      }

      while (i <= numberOfPages) {
        if (l <= i && i <= r) {
          pagingInt.push({
            page: i,
            title: i.toString(),
          })
        }
        i++
      }

      const pagePlusVisible = page + visiblePagingHalfRange
      if (pagePlusVisible < numberOfPages && visiblePagingHalfRange > 1) {
        pagingInt.push({
          page: 0,
          title: '...',
          disabled: true,
        })
        pagingInt.push({
          page: numberOfPages,
          title: numberOfPages.toString(),
        })
      }

      if (page < numberOfPages) {
        pagingInt.push({
          page: page + 1,
          title: 'Next',
        })
      }
      this.setState({
        paging: pagingInt,
      })
    }
  }

  render() {
    const itemsPerPageArr = [20, 50, 100]
    const {
      orderFormDict,
      orderFormTenantDict,
      filteredSteps,
      loading,
      orderFormsForActions,
      types,
      availableStatusFilters,
      tenants,
    } = this.state
    const { groupInFilter, companyInFilter, statusInFilter, typeInFilter, rws } = this.state.requestFilter

    const { t, user, i18n } = this.props
    const { language } = i18n

    const start = (this.state.page - 1) * rws
    const end = rws > 0 ? Math.min(start + rws, filteredSteps?.length) : filteredSteps?.length
    if (!!start && !!end && start === end) {
      this.setState({
        page: this.state.page - 1,
      })
    }
    const paged = end !== 0 ? filteredSteps?.slice(start, end) : filteredSteps
    const driverName = 'DR002'
    const driverSurname = 'DR004'
    const ltrDriver = 'LTR_driver'
    const fuelCardDriverName = 'OV005'
    const internalReference = 'InternalReference'

    return (
      <Loader blocking={loading}>
        <>
          <div className="reqArea">
            <div className="header-filters-example">
              <div className="contractFilters reqFilters">
                <div className="leftContainer">
                  <div className="reqTitle">
                    <i className="icon-requests" />
                    <span>{t('REQUESTS')}</span>
                  </div>
                  {user?.isInternalUser && !mobileVersion && (
                    <div className="perPage group">
                      <div className="selectArea">
                        <select onChange={(e) => this.onGroupFilterChange(e)}>
                          <option value={''}>
                            {t('ALL')} {t('GROUPS')}
                          </option>
                          {tenants
                            .filter((te) => orderFormTenantDict[te.id])
                            .map((te, i) => (
                              <option key={i} selected={te.id === groupInFilter} value={te.id}>
                                {te.name}
                              </option>
                            ))}
                        </select>
                        <i className="icon-bottom" />
                      </div>
                    </div>
                  )}
                  {mobileVersion && (
                    <div className="selectArea">
                      <div className="mobileFilter">
                        {statusInFilter !== 'active' ? (
                          <i className={`icon-${this.insertIconClassName(statusInFilter)}`} />
                        ) : (
                          t('ALL')
                        )}
                        <i onClick={() => this.switchMobileStatuses()} className="icon-bottom" />
                      </div>
                      {this.state.showMobileStatuses && (
                        <div className="mobileStatuses">
                          <div onClick={() => this.onStatusFilterDivChange('active')}>{t('ALL')}</div>
                          {['pending', 'approved', 'declined'].map((el) => (
                            <div key={el} onClick={() => this.onStatusFilterDivChange(el)}>
                              {' '}
                              <i className={`icon-${this.insertIconClassName(el)}`} />{' '}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={mobileVersion ? { width: 'auto' } : undefined} className="companySearchBar">
                    <input
                      type="text"
                      name={`fieldnameveryunique${new Date().getTime()}`}
                      autoComplete="none"
                      list="autocompleteOff"
                      placeholder={mobileVersion ? '' : t('SEARCH_BY_COMPANY_DRIVER_CREATEDBY')}
                      className="companyFilter"
                      onChange={(e) => this.filterByCompany(e)}
                      value={companyInFilter}
                    />
                    {companyInFilter && (
                      <button className="clearButton" onClick={() => this.clearCompanyFilter()}>
                        <span className="icon-clear"></span>
                      </button>
                    )}
                    <i className="icon-search" />
                  </div>
                  {user?.canManageOrders && orderFormsForActions.length > 0 && !mobileVersion && (
                    <div className="quickActions">
                      <div className="actionTrigger">
                        <i className="icon-actions" />
                        {t('ACTIONS')}
                        <i className="icon-bottom" />
                      </div>
                      <ul className="quickActionsList">
                        {orderFormsForActions.map((f, i) => {
                          const tooltip = Orders.getFormGenericTooltip(f, language)
                          return (
                            <li key={i}>
                              <NavLink to={`/requests/new/${f.id}`} exact={true}>
                                <i className={f.icon || 'icon-order'} /> {Orders.getFormTitle(f, language) || f.id}
                                {!!tooltip && (
                                  <div className="tooltipArea">
                                    <i className="icon-info" />
                                    <span className="generic-tooltip">{tooltip}</span>
                                  </div>
                                )}
                              </NavLink>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="rightFilters">
                  {!mobileVersion && (
                    <div className="perPage">
                      <div className="selectArea">
                        <select onChange={(e) => this.onTypeFilterChange(e)}>
                          <option value={''}>{t('ALL')}</option>
                          {types
                            .map((obj, i) => (
                              <option key={i} value={obj} selected={obj === typeInFilter}>
                                {Orders.getFormTitle(orderFormDict[obj]!, language)}
                              </option>
                            ))
                            .sort((a, b) => a.props.children?.localeCompare(b.props.children))}
                        </select>
                        <i className="icon-bottom" />
                      </div>
                      {t('TYPE')}
                    </div>
                  )}
                  {!mobileVersion && (
                    <div className="perPage">
                      <div className="selectArea">
                        <select onChange={(e) => this.onStatusFilterChange(e)} value={statusInFilter}>
                          {availableStatusFilters.all && <option value={'all'}>{t('ALL')}</option>}
                          {availableStatusFilters.active && <option value={'active'}>{t('ACTIVE')}</option>}
                          {availableStatusFilters.draft && <option value={'draft'}>{t('DRAFT')}</option>}
                          {availableStatusFilters.sent && <option value={'sent'}>{t('SENT')}</option>}
                          {availableStatusFilters.archived && !user?.isGuest && (
                            <option value={'archived'}>{t('ARCHIVED')}</option>
                          )}
                          {availableStatusFilters.cancelled && !user?.isGuest && (
                            <option value={'cancelled'}>{t('CANCELED')}</option>
                          )}
                          {availableStatusFilters.completed && !user?.isGuest && (
                            <option value={'completed'}>{t('COMPLETED')}</option>
                          )}
                          <option disabled>----------</option>
                          {availableStatusFilters.pending && !user?.isGuest && (
                            <option value={'pending'}>{t('STATUS_PENDING')}</option>
                          )}
                          {availableStatusFilters.approved && !user?.isGuest && (
                            <option value={'approved'}>{t('STATUS_APPROVED')}</option>
                          )}
                          {availableStatusFilters.declined && !user?.isGuest && (
                            <option value={'declined'}>{t('STATUS_DECLINED')}</option>
                          )}
                        </select>
                        <i className="icon-bottom" />
                      </div>
                      {t('STATUS')}
                    </div>
                  )}

                  {!mobileVersion && (
                    <div className="perPage">
                      <div className="selectArea">
                        <select onChange={(e) => this.setRowsPerPage(e.target.value)} value={rws}>
                          {itemsPerPageArr.map((ipp, idx) => (
                            <option key={idx} selected={rws === ipp} value={ipp}>
                              {ipp}
                            </option>
                          ))}
                          <option value={'0'}>{t('ALL')}</option>
                        </select>
                        <i className="icon-bottom" />
                      </div>
                      {t('REQUESTS_PER_PAGE')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="scrollableArea">
              <div
                className={`grid-wrap ${rws > 0 && filteredSteps.length > rws ? `flat ${rws}page` : 'flat-no-paging'}`}
              >
                {!mobileVersion && (
                  <ul className="reqHeader">
                    <li>
                      <div className="type" title={t('TYPE')}>
                        {t('TYPE')}
                      </div>
                      <div className="id" title={t('COMPANY')}>
                        {t('COMPANY')}
                      </div>
                      {/* <div className="id">ID</div> */}
                      <div className="date" title={t('OWNER')}>
                        {t('OWNER')}
                      </div>
                      <div className="date" title={t('DRIVER')}>
                        {t('DRIVER')}
                      </div>
                      <div className="dateModified" title={t('MODIFIED')}>
                        {t('MODIFIED')}
                      </div>
                      <div className="date" title={t('LOGISTICID')}>
                        {t('LOGISTICID')}
                      </div>
                      <div className="date" title={t('INTERNAL_REFERENCE')}>
                        {t('INTERNAL_REFERENCE')}
                      </div>
                      <div className="steps" title={t('APPROVAL_STEPS')}>
                        {t('APPROVAL_STEPS')}
                      </div>
                      <div className="status" title={t('STATUS')}>
                        {t('STATUS')}
                      </div>
                      <div className="actions" title={t('ACTIONS')}>
                        {t('ACTIONS')}
                      </div>
                    </li>
                  </ul>
                )}
                <ul className="reqList">
                  {paged &&
                    paged.map((o, i) => {
                      const internalStatus = this.getInternalStatus(o.rowItem.order)
                      return (
                        <>
                          <li
                            key={i}
                            className={
                              o.rowItem?.order?.approvalProcess ? this.orderClassName(o?.rowItem?.order) : undefined
                            }
                          >
                            <div className="type">
                              <div className="typeArea" title={Orders.getFormTitle(o.rowItem.form, language)}>
                                <i className={orderFormDict[o.rowItem.order.formId]?.icon || ''} />
                                <span>
                                  <NavLink to={`/requests/${o.rowItem.order.id}`} exact={true}>
                                    {Orders.getFormTitle(o.rowItem.form, language)}
                                  </NavLink>
                                </span>
                              </div>
                              {mobileVersion && (
                                <div className="typeArea">
                                  <i className="icon-manager" />
                                  <span>{o.rowItem.order.userDisplayName}</span>
                                </div>
                              )}
                            </div>
                            {!mobileVersion && (
                              <>
                                <div className="id">
                                  <div title={o.rowItem.order?.company?.companyName}>
                                    <span>{o.rowItem.order?.company?.companyName}</span>
                                  </div>
                                </div>
                                <div className="date">
                                  <span>{o.rowItem.order.userDisplayName}</span>
                                </div>
                                {o.rowItem?.order?.data[fuelCardDriverName] ? (
                                  <div className="date" title={o.rowItem?.order?.data[fuelCardDriverName]}>
                                    <span>{o.rowItem?.order?.data[fuelCardDriverName]}</span>
                                  </div>
                                ) : o.rowItem?.order?.data[ltrDriver] ? (
                                  <div className="date" title={o.rowItem?.order?.data[ltrDriver]}>
                                    <span>{o.rowItem?.order?.data[ltrDriver]}</span>
                                  </div>
                                ) : (
                                  <div
                                    className="date"
                                    title={
                                      o.rowItem.order.data[PL001] === 'rad1'
                                        ? 'Poolcar'
                                        : `${o.rowItem.order.data[driverName] || ''} ${
                                            o.rowItem.order.data[driverSurname] || ''
                                          }`
                                    }
                                  >
                                    <span>
                                      {o.rowItem.order.data[PL001] === 'rad1'
                                        ? 'Poolcar'
                                        : `${o.rowItem.order.data[driverName] || ''} ${
                                            o.rowItem.order.data[driverSurname] || ''
                                          }`}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                            <div className="dateModified">
                              {printDate(new Date(o.rowItem.order.modified!)) >=
                              printDate(new Date(o.rowItem.order.created!))
                                ? printDate(new Date(o.rowItem.order.modified!))
                                : null}
                            </div>
                            {!mobileVersion && (
                              <>
                                <div className="date">
                                  <span>{o.rowItem.order.logisticId || ''}</span>
                                  {o.rowItem.order.logisticId && o.rowItem.order.logisticId.length > 20 && (
                                    <div className="tooltip">
                                      <div>{o.rowItem.order.logisticId}</div>
                                    </div>
                                  )}
                                </div>
                                <div className="date">
                                  <span>{o.rowItem.order.data[internalReference] || ''}</span>
                                  {o.rowItem.order.data[internalReference] &&
                                    o.rowItem.order.data[internalReference].length > 20 && (
                                      <div className="tooltip">
                                        <div>{o.rowItem.order.data[internalReference] || ''}</div>
                                      </div>
                                    )}
                                </div>
                                <div className="steps">
                                  <div className="stepList">
                                    {o.mainSteps.map((step, i) => (
                                      <div
                                        key={i}
                                        className={`stepArea ${this.stepsClassName(step.status, i, o.mainSteps)}`}
                                      >
                                        {!o.autoApprove && (
                                          <span className="tooltipText">{`${t('APPROVAL_STEP')} - ${step.step}`}</span>
                                        )}
                                        <div className="step">
                                          <i className={`icon-${this.insertIconClassName(step.status)}`} />
                                          {!o.autoApprove ? (
                                            <span>{!this.state.loading && `${t('APPROVAL_STEP')} - ${step.step}`}</span>
                                          ) : (
                                            <span>{!this.state.loading && t('SENT')}</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                    {o.otherSteps.length > 0 && (
                                      <div className="stepArea">
                                        {o.otherSteps.map((step, i) => (
                                          <div className={`step ${this.stepsClassName(step.status, i, o.mainSteps)}`}>
                                            <i className={`icon-${this.insertIconClassName(step.status)}`} />
                                            <span>
                                              {t('APPROVAL_STEP')} - {step.step}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {o.otherSteps.length > 0 && (
                                      <div className="stepArea">
                                        {o.otherSteps.map((step, i) => (
                                          <div className={`step ${this.stepsClassName(step.status, i, o.mainSteps)}`}>
                                            <i className={`icon-${this.insertIconClassName(step.status)}`} />
                                            <span>
                                              {t('APPROVAL_STEP')} - {step.step}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                            <div className="status">
                              {internalStatus && !mobileVersion ? (
                                <span>{t(internalStatus.toUpperCase())}</span>
                              ) : (
                                <span>
                                  {o.rowItem.order.approvalProcess ? (
                                    o.rowItem.order.approvalProcess.status === 'Approved' && o.autoApprove ? (
                                      mobileVersion ? (
                                        <i className="icon-check" />
                                      ) : (
                                        t('SENT')
                                      )
                                    ) : mobileVersion ? (
                                      <i className={`icon-${this.orderClassName(o.rowItem.order, true)}`} />
                                    ) : (
                                      t(o.rowItem.order.approvalProcess?.status?.toUpperCase() ?? '')
                                    )
                                  ) : (
                                    t('DRAFT')
                                  )}
                                </span>
                              )}
                              {mobileVersion && (
                                <i
                                  style={{ paddingLeft: '5px' }}
                                  onClick={() => this.showAdditionalInfo(o.rowItem.order.id!)}
                                  className={`icon-${
                                    this.state.expandedOrders.includes(o.rowItem.order.id!) ? 'bottom' : 'right'
                                  }`}
                                />
                              )}
                            </div>
                            {!mobileVersion && (
                              <div className="actions">
                                {user?.isInternalUser && (
                                  <a title="Alternative view">
                                    <NavLink to={`/requests/${o.rowItem.order.id}/view=dcs`} exact={true}>
                                      <i className="icon-focus" />
                                    </NavLink>
                                  </a>
                                )}
                                {user?.isInternalUser && (
                                  <>
                                    {o.rowItem.order.archived ? (
                                      <a
                                        title={t('RESTORE_ORDER_FROM_ARCHIVE')}
                                        onClick={() => this.archiveOrRestoreOrder(o.rowItem.order.id!, true)}
                                      >
                                        <i className="icon-unarchive" />
                                      </a>
                                    ) : (
                                      <a
                                        title={t('ARCHIVE_ORDER')}
                                        onClick={() => this.archiveOrRestoreOrder(o.rowItem.order.id!)}
                                      >
                                        <i className="icon-archive2" />
                                      </a>
                                    )}
                                  </>
                                )}
                                {user?.canManageOrders && user?.id === o.rowItem.order.userId && (
                                  <a className={'duplicate-btn-visible'} onClick={() => this.showDuplicateConfirm(o)}>
                                    <i className="icon-duplicate" />
                                  </a>
                                )}
                                <a
                                  className={
                                    user?.canManageOrders &&
                                    ((user?.id === o.rowItem.order.userId &&
                                      o.rowItem.order.approvalProcess?.status !== 'Approved') ||
                                      user?.isAdmin)
                                      ? 'delete-btn-visible'
                                      : 'delete-btn-hidden'
                                  }
                                  title={t('DELETE')}
                                  onClick={() => this.showDeleteConfirm(o)}
                                >
                                  <i className="icon-clear" />
                                </a>
                              </div>
                            )}
                            {this.state.expandedOrders.includes(o.rowItem.order.id!) && mobileVersion && (
                              <div className="addInfoParent">
                                <div className="addInfoContent">
                                  <div className="addInfoTop">
                                    <div className="addInfoCustomer">
                                      <i className="icon-customers" />
                                      <span>{o.rowItem.order?.company?.companyName || '-'}</span>
                                    </div>
                                    <div className="addInfoDriver">
                                      <i className="icon-driver" />{' '}
                                      <span>{o.rowItem?.order?.data[fuelCardDriverName] || '-'}</span>
                                    </div>
                                  </div>
                                  <div className="separator" />
                                  <div className="addInfoBottom">
                                    <span className="approvedSteps">approved steps: {o.approvedSteps}</span>
                                    {o.rowItem.order.approvalProcess?.status === ApprovalProcessStatus.Declined ? (
                                      <span className="declinedSteps">declined steps: {o.declinedSteps}</span>
                                    ) : (
                                      <span className="pendingSteps">pending steps: {o.pendingSteps}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </li>
                        </>
                      )
                    })}
                </ul>
              </div>
            </div>
          </div>
          <div className="paging-row">
            {this.state.numberOfPages > 1 && rws > 0 && (
              <div className="paging">
                {this.state.paging.map((p: IPagingItem, index) => {
                  return (
                    <button
                      className={p.page === this.state.page ? 'current' : ''}
                      key={index}
                      disabled={p.disabled}
                      onClick={() => {
                        this.selectPage(p.page)
                      }}
                    >
                      {p.title}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          {!loading && paged && paged.length === 0 && (
            <div className="noRequests">{t('CURRENT_SELECTION_HAS_NO_DATA')}</div>
          )}
        </>
        <Confirm
          visible={this.state.showDeleteConfirm}
          onClose={(confirm: boolean) => {
            this.handleConfirmClose(confirm)
          }}
          title={t('PLEASE_CONFIRM')}
          message={`${t('DO_YOU_WANT_TO_DELETE_THE_REQUEST')}?`}
        />
        <Confirm
          visible={this.state.showDuplicateConfirm}
          onClose={(confirm: boolean) => {
            this.handleDuplicateClose(confirm)
          }}
          title={t('PLEASE_CONFIRM')}
          message={`${t('DO_YOU_WANT_TO_DUPLICATE_THE_REQUEST')}`}
        />
        <div />
      </Loader>
    )
  }

  private getInternalStatus(order: OrderViewModel) {
    switch (order.internalOrderStatus?.toLowerCase()) {
      case 'pending':
      case 'onhold':
      case 'inprogress':
        return 'inprogress'
      case 'done':
      case 'processed':
        return 'completed'
      case 'cancelled':
        return 'canceled'
      case 'archived':
        return 'archived'
      default:
        return undefined
    }
  }

  private switchMobileStatuses() {
    this.setState({
      showMobileStatuses: !this.state.showMobileStatuses,
    })
  }

  private prepareFormsToRender() {
    const { user, i18n } = this.props
    const { orderForms, tenants } = this.state
    const { groupInFilter } = this.state.requestFilter
    const tenantIds = tenants?.map((t) => t.id) || []
    const orderFormsFiltered =
      user?.isInternalUser && !!groupInFilter ? orderForms.filter((f) => f.ownerTenant === groupInFilter) : orderForms
    const { language } = i18n
    const orderFormsForActions = (
      tenantIds.length > 0 ? orderFormsFiltered.filter((f) => tenantIds.includes(f.ownerTenant)) : orderFormsFiltered
    )
      .filter((f) => Orders.isFormAvailable(f, user, this.state.customers))
      .sort((a, b) => Orders.getFormTitle(a, language).localeCompare(Orders.getFormTitle(b, language)))

    this.setState({
      orderFormsForActions,
    })
  }

  private clearCompanyFilter() {
    const requestFilter = { ...this.state.requestFilter }
    requestFilter.companyInFilter = ''

    this.setState({
      requestFilter,
    })
  }

  private filterByCompany(e: React.ChangeEvent<HTMLInputElement>) {
    const requestFilter = { ...this.state.requestFilter }
    requestFilter.companyInFilter = e.target.value

    this.setState({
      requestFilter,
    })
  }

  private showDuplicateConfirm(req: StepsToGrid) {
    this.setState({
      showDuplicateConfirm: true,
      selectedRequest: req,
    })
  }

  private showDeleteConfirm(req: StepsToGrid) {
    this.setState({
      showDeleteConfirm: true,
      selectedRequest: req,
    })
  }

  private handleDuplicateClose(confirm: boolean) {
    if (confirm) {
      this.setState({
        loading: true,
      })
      this.props.getOrder(this.state.selectedRequest!.rowItem.order.id).then(
        (order) => {
          const dataToCopy = { ...order.data }
          const selectedForm = this.state.orderForms.find((o) => o.id === order.formId)

          selectedForm?.formFields.map((field) => {
            if (field.avoidToDuplicate) {
              delete dataToCopy[field.name]
            }
          })

          this.props
            .submitForm(false, {
              company: order.company,
              costCenter: order.costCenter,
              formId: order.formId,
              data: dataToCopy,
              approvalProcessConfigId: order.approvalProcessConfigId,
            })
            .then(() => this.reload())
        },
        () => this.setState({ loading: false })
      )
    }
    this.setState({
      showDuplicateConfirm: !this.state.showDuplicateConfirm,
    })
  }

  private handleConfirmClose(confirm: boolean) {
    if (confirm && this.state.selectedRequest) {
      this.setState({
        loading: true,
      })
      this.props.deleteOrder(this.state.selectedRequest.rowItem.order.id).then(() => this.reload())
    }
    this.setState({
      showDeleteConfirm: !this.state.showDeleteConfirm,
    })
  }

  private archiveOrRestoreOrder(orderId: string, restore?: boolean) {
    this.setState({
      loading: true,
    })

    const promise = restore ? this.props.restoreOrderFromArchive(orderId) : this.props.archiveOrder(orderId)
    promise.then(
      () => this.reload(),
      () => {
        this.setState({
          loading: false,
        })
      }
    )
  }

  private selectPage(p: number) {
    this.setState({
      page: p,
    })
  }

  private setRowsPerPage(rowsPerPage: string) {
    const requestFilter = { ...this.state.requestFilter }

    requestFilter.rws = parseInt(rowsPerPage, 10)

    this.setState({
      page: 1,
      requestFilter,
    })
  }

  private onStatusFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const statusInFilter = e.target.value as keyof typeof statusFilters
    this.setState({
      requestFilter: {
        ...this.state.requestFilter,
        statusInFilter,
      },
      page: 1,
    })
  }

  private onStatusFilterDivChange(status: string) {
    const requestFilter = { ...this.state.requestFilter }

    requestFilter.statusInFilter = status as keyof typeof statusFilters

    this.setState({
      requestFilter,
      page: 1,
      showMobileStatuses: false,
    })
  }

  private onGroupFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const groupInFilter = e.target.value
    const requestFilter = { ...this.state.requestFilter }

    requestFilter.groupInFilter = groupInFilter
    requestFilter.typeInFilter = ''

    this.setState({
      requestFilter,
    })
  }

  private onTypeFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const typeInFilter = e.target.value
    const requestFilter = { ...this.state.requestFilter }

    requestFilter.typeInFilter = typeInFilter

    this.setState({
      requestFilter,
    })
  }

  private insertIconClassName(e: string) {
    const status = e.toLowerCase()
    if (status === 'approved') {
      return 'check'
    }
    if (status === 'declined') {
      return 'denied'
    }
    if (status === 'pending') {
      return 'pending'
    }
  }

  private stepsClassName(e: string, index: number, allSteps: ApprovalProcessStepViewModel[]) {
    if (e === 'Approved') {
      return 'checked'
    }
    if (e === 'Declined') {
      return 'denied'
    }
    if (e === null) {
      return null
    }
    if (e === 'Pending') {
      return index > 0 ? (allSteps[index - 1].status === 'Approved' ? 'pending' : 'pending gray') : 'pending'
    }
  }

  private showAdditionalInfo(id: string) {
    const expandedOrders = [...this.state.expandedOrders]
    if (expandedOrders.includes(id)) {
      expandedOrders.splice(expandedOrders.indexOf(id), 1)
    } else {
      expandedOrders.push(id)
    }
    this.setState({
      expandedOrders,
    })
  }

  private orderClassName(order: OrderViewModel, icon?: boolean) {
    if (!mobileVersion) {
      if (
        order.internalOrderStatus === 'InProgress' ||
        order.internalOrderStatus === 'OnHold' ||
        order.internalOrderStatus === 'Pending'
      ) {
        return 'pending'
      }
      if (
        order.internalOrderStatus === 'Done' ||
        order.internalOrderStatus === 'Processed' ||
        order.internalOrderStatus === 'Archived'
      ) {
        return icon ? 'check' : 'checked'
      }
      if (order.internalOrderStatus === 'Cancelled') {
        return 'denied'
      }
    }

    if (order.approvalProcess?.status === 'Approved') {
      return icon ? 'check' : 'checked'
    }
    if (order.approvalProcess?.status === 'Declined') {
      return 'denied'
    }
    if (order.approvalProcess?.status === null) {
      return undefined
    }
    if (order.approvalProcess?.status === 'Pending') {
      return 'pending'
    }
  }

  private ordersInGrid() {
    const { filteredOrders, approvalProcessConfigDict } = this.state
    const { orderForms } = this.state
    const stepsInGrid =
      filteredOrders.length > 0
        ? filteredOrders
            .filter((o) => o.order.formId && o.order.formId !== emptyGuid)
            .map((object) => {
              const steps = approvalProcessConfigDict[object.order.approvalProcessConfigId!]?.steps
              const item: StepsToGrid = {
                rowItem: object,
                mainSteps: [],
                otherSteps: [],
                unusedSteps: [],
                autoApprove: (!!steps && steps[0]?.autoApprove) || false,
              }
              if (object.order.approvalProcess) {
                if (object.order.approvalProcess.steps?.length) {
                  item.mainSteps = object.order.approvalProcess.steps
                  item.approvedSteps = object.order.approvalProcess.steps.filter(
                    (el) => el.status === ApprovalProcessStepStatus.Approved
                  )?.length
                  item.pendingSteps = object.order.approvalProcess.steps.filter(
                    (el) => el.status === ApprovalProcessStepStatus.Pending
                  )?.length
                  item.declinedSteps = object.order.approvalProcess.steps.filter(
                    (el) => el.status === ApprovalProcessStepStatus.Declined
                  )?.length
                }
                if (orderForms) {
                  orderForms.forEach((y) =>
                    y.approvalProcessConfigs.map((z) => {
                      for (let i = object.order.approvalProcess?.steps?.length ?? 0; i < z.totalSteps; i++) {
                        item.unusedSteps.push(i)
                      }
                    })
                  )
                } else {
                  item.otherSteps = object.order.approvalProcess?.steps?.slice(2) ?? []
                  item.mainSteps = object.order.approvalProcess?.steps?.slice(0, 2) ?? []
                }
              }
              return item
            })
        : []
    stepsInGrid.sort(
      (a, b) => new Date(b.rowItem.order.modified!).getTime() - new Date(a.rowItem.order.modified!).getTime()
    )
    this.setState({
      stepsInGrid,
      filteredSteps: stepsInGrid,
    })
  }

  private filterOrder(order: OrderViewModel) {
    const { currentUserCustomerMap, currentUserUnitMap } = this.state

    const { user } = this.props
    if (mobileVersion && order.approvalProcess === null) {
      return false
    }
    if (user?.isInternalUser || order?.userId === user?.id) {
      return true
    }

    const matchingSteps: ApprovalProcessStepViewModel[] = []

    const sortedSteps = order.approvalProcess?.steps?.toSorted((a, b) => a.step - b.step)
    const nextPendingSteps = sortedSteps?.filter((s) => s.status === ApprovalProcessStepStatus.Pending) || []
    const approvedSteps = sortedSteps?.filter((s) => s.status === ApprovalProcessStepStatus.Approved) || []
    const declinedSteps = sortedSteps?.filter((s) => s.status === ApprovalProcessStepStatus.Declined) || []

    if (nextPendingSteps.length > 0) {
      matchingSteps.push(nextPendingSteps[0])
    }
    if (approvedSteps.length > 0) {
      matchingSteps.push(...approvedSteps)
    }
    if (declinedSteps.length > 0) {
      matchingSteps.push(...declinedSteps)
    }
    if (matchingSteps.length > 0) {
      return matchingSteps.some((s) => {
        switch (s.stepType) {
          case ApprovalProcessStepKind.BusinessUnit:
            return s.owners.some((o) => currentUserUnitMap[o])
          case ApprovalProcessStepKind.Company:
            return s.owners.some((o) => currentUserCustomerMap[o])
          default:
            return s.owners.some((o) => user?.id === o)
        }
      })
    }

    return false
  }

  private filterOrders() {
    const { orders } = this.state
    const { orderFormDict, approvalProcessConfigDict, requestFilter } = this.state
    const { groupInFilter, companyInFilter, typeInFilter, statusInFilter } = requestFilter
    const types = new Set<string>()
    const statuses: string[] = []

    const ordersSource = mobileVersion ? orders.filter((o) => o.approvalProcess) : orders
    const ordersWithForms: GridRow[] = ordersSource
      .filter((o) => orderFormDict[o.formId] && this.filterOrder(o))
      .map((order) => {
        const gridItem: GridRow = {
          order,
          form: orderFormDict[order.formId]!,
          approvalProcessConfig: order.approvalProcessConfigId
            ? approvalProcessConfigDict[order.approvalProcessConfigId]
            : undefined,
        }
        return gridItem
      })

    const availableStatusFilters: Partial<typeof statusFilters> = {}
    for (const item of ordersWithForms) {
      if (item.form && (item.form.ownerTenant === groupInFilter || !groupInFilter)) {
        types.add(item.form.id)
      }
      for (const key in statusFilters) {
        const filterKey = key as keyof typeof statusFilters
        if (statusFilters[filterKey](item)) {
          availableStatusFilters[filterKey] = statusFilters[filterKey]
        }
      }
    }

    let newRequestFilter = requestFilter
    const statusFilter = availableStatusFilters[statusInFilter]
    if (!statusFilter) {
      const validKey = Object.keys(availableStatusFilters).at(0)
      if (validKey) {
        newRequestFilter = {
          ...requestFilter,
          statusInFilter: validKey as keyof typeof statusFilters,
        }
      }
    }

    const filteredOrders = ordersWithForms.filter(
      (o) =>
        statusFilter?.(o) &&
        companyFilter(o, companyInFilter) &&
        formFilter(o, typeInFilter) &&
        tenantFilter(o, groupInFilter)
    )

    this.setState({
      filteredOrders,
      availableStatusFilters,
      statuses: statuses,
      types: Array.from(types),
      requestFilter: newRequestFilter,
    })
  }
}

function mapStateToProps({ appData }: State) {
  return {
    user: appData.currentUser,
  }
}

const mapDispatchToProps = {
  getCustomers: wrapQuery(() => customersApi.endpoints.getAllCustomers.initiate()),
  getUnits: wrapQuery(() => customersApi.endpoints.getAllUnits.initiate()),
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
  getOrdersForms: wrapQuery(() => ordersApi.endpoints.getOrdersForms.initiate()),
  getOrder: wrapQuery((id: string) => ordersApi.endpoints.getOrder.initiate(id)),
  getOrders: wrapQuery(() => ordersApi.endpoints.getOrders.initiate()),
  deleteOrder: wrapQuery((id: string) => ordersApi.endpoints.deleteOrder.initiate(id)),
  submitForm: wrapQuery((final: boolean, model: OrderInputModel) =>
    ordersApi.endpoints.postOrder.initiate({ final, model })
  ),
  archiveOrder: wrapQuery((id: string) => ordersApi.endpoints.archiveOrder.initiate(id)),
  restoreOrderFromArchive: wrapQuery((id: string) => ordersApi.endpoints.restoreOrderFromArchive.initiate(id)),
}

const connector = connect(mapStateToProps, mapDispatchToProps)

// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(RequestList))
